/**
 * HrvDashboard — 30-day biofeedback trends + recent session snapshots.
 *
 * Mounted inside the Beat Settings card directly below HeartMonitorPanel
 * so the user never has to navigate away to see their trends.
 *
 * Design intent:
 *   - Fast empty-state: fresh users see a friendly "Pair a strap and
 *     record your first session" CTA rather than a broken empty chart.
 *   - Pure SVG sparkline (no chart lib dep) — one data point per calendar
 *     day. RMSSD line = cyan, Rhythm line = rose. Null days drop the line
 *     to create natural gaps so missing data doesn't interpolate misleading
 *     values.
 *   - Opt-in fetch: the panel only polls when expanded. Saves bandwidth on
 *     mobile, avoids hydrating the list for users who never pair a strap.
 *   - Lightweight API surface: GET /api/hrv/trend returns the full chart
 *     payload in a single round-trip (trend array + summary scalars).
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { TrendingUp, RefreshCw, Loader2 } from 'lucide-react';
import { Button } from './ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatDateShort = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
};

const formatDuration = (sec) => {
  if (sec == null) return '—';
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}m ${s.toString().padStart(2, '0')}s`;
};

/**
 * Sparkline — pure SVG. Builds two polylines from the trend array.
 *
 * Normalises each metric independently so both curves use the full
 * vertical range. Gaps (null values) split the polyline into separate
 * segments via a break — looks less like interpolation.
 */
const Sparkline = ({ trend }) => {
  const width = 520;
  const height = 110;
  const pad = 8;

  const rmssdPath = useMemo(() => buildPath(trend.map((t) => t.avg_rmssd), width, height, pad), [trend]);
  const rhythmPath = useMemo(() => buildPath(trend.map((t) => t.avg_rhythm), width, height, pad), [trend]);

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      className="w-full h-24"
      data-testid="hrv-sparkline"
    >
      {/* subtle gridlines — 4 horizontal bands */}
      {[0.25, 0.5, 0.75].map((f) => (
        <line
          key={f}
          x1={pad} x2={width - pad}
          y1={height * f} y2={height * f}
          stroke="rgba(255,255,255,0.05)" strokeWidth={1}
        />
      ))}
      {rmssdPath.map((d, i) => (
        <path key={`r-${i}`} d={d} stroke="#67e8f9" strokeWidth={1.8} fill="none" strokeLinecap="round" />
      ))}
      {rhythmPath.map((d, i) => (
        <path key={`h-${i}`} d={d} stroke="#fb7185" strokeWidth={1.8} fill="none" strokeLinecap="round" strokeOpacity={0.85} />
      ))}
    </svg>
  );
};

// Build an array of SVG path strings (one per contiguous non-null run).
function buildPath(values, width, height, pad) {
  const clean = values.map((v) => (typeof v === 'number' && isFinite(v) ? v : null));
  const min = Math.min(...clean.filter((v) => v !== null));
  const max = Math.max(...clean.filter((v) => v !== null));
  if (!isFinite(min) || !isFinite(max)) return [];
  const range = max - min || 1;
  const xStep = (width - pad * 2) / Math.max(1, values.length - 1);

  const segments = [];
  let current = [];
  clean.forEach((v, i) => {
    if (v == null) {
      if (current.length > 1) segments.push(current);
      current = [];
      return;
    }
    const x = pad + i * xStep;
    const y = height - pad - ((v - min) / range) * (height - pad * 2);
    current.push([x, y]);
  });
  if (current.length > 1) segments.push(current);

  return segments.map((pts) => {
    const [first, ...rest] = pts;
    return `M${first[0].toFixed(1)},${first[1].toFixed(1)} ` +
           rest.map((p) => `L${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');
  });
}

export const HrvDashboard = () => {
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [trend, setTrend] = useState([]);
  const [summary, setSummary] = useState(null);
  const [snapshots, setSnapshots] = useState([]);
  const [moodTrend, setMoodTrend] = useState([]);
  const [moodSummary, setMoodSummary] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [tRes, sRes, mRes] = await Promise.all([
        fetch(`${API}/hrv/trend?days=30`, { credentials: 'include' }),
        fetch(`${API}/hrv/snapshots?days=30&limit=10`, { credentials: 'include' }),
        fetch(`${API}/mood/trend?days=30`, { credentials: 'include' }),
      ]);
      if (!tRes.ok || !sRes.ok) {
        // 401 = anon user. Show the empty-state, not an error.
        if (tRes.status === 401 || sRes.status === 401) {
          setTrend([]); setSummary(null); setSnapshots([]);
          setMoodTrend([]); setMoodSummary(null);
          setError('signin');
        } else {
          setError('fetch');
        }
        return;
      }
      const t = await tRes.json();
      const s = await sRes.json();
      setTrend(t.trend || []);
      setSummary(t.summary || null);
      setSnapshots(s.snapshots || []);
      // Mood is best-effort — never block the rest of the dashboard on it.
      if (mRes.ok) {
        const m = await mRes.json();
        setMoodTrend(m.trend || []);
        setMoodSummary(m.summary || null);
      }
    } catch (e) {
      setError('fetch');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch only on first expand — saves a call for users who never open
  // the panel. Re-fetch on manual refresh only.
  useEffect(() => {
    if (expanded && !summary && !loading) load();
  }, [expanded, summary, loading, load]);

  const hasData = summary && summary.totalSessions > 0;

  return (
    <div
      className="rounded-lg border border-cyan-400/15 bg-cyan-500/[0.03] p-4 space-y-3"
      data-testid="hrv-dashboard-panel"
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        data-testid="hrv-dashboard-toggle"
        className="w-full flex items-center justify-between gap-2 text-left"
      >
        <div className="flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-cyan-300" />
          <h4 className="text-sm font-medium text-white">HRV dashboard</h4>
          <span className="text-[10px] uppercase tracking-wider text-white/35 ml-1">
            30 days
          </span>
        </div>
        <span className="text-white/40 text-xs">
          {expanded ? 'Hide' : 'Show'}
        </span>
      </button>

      {expanded && (
        <div className="space-y-3 pt-2 border-t border-white/5">
          {loading && (
            <div className="flex items-center justify-center py-6 text-white/50 text-sm gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading your trends…
            </div>
          )}

          {!loading && error === 'signin' && (
            <p className="text-xs text-white/50 text-center py-4" data-testid="hrv-dashboard-signin">
              Sign in to see your 30-day HRV trends.
            </p>
          )}

          {!loading && error === 'fetch' && (
            <div className="text-xs text-rose-300 text-center py-4" data-testid="hrv-dashboard-error">
              Couldn't load. <button onClick={load} className="underline">Retry</button>
            </div>
          )}

          {!loading && !error && !hasData && (
            <div className="space-y-3" data-testid="hrv-dashboard-empty">
              <div className="text-center py-4 space-y-1">
                <p className="text-sm text-white/60">No sessions yet.</p>
                <p className="text-xs text-white/35">
                  Pair a heart monitor and run a session — your trends appear here.
                </p>
              </div>
              {/* Mood works without a heart monitor, so show the rating
                  summary here if the user has any ratings at all. */}
              {moodSummary && moodSummary.totalRatings > 0 && (
                <div
                  className="flex items-center justify-between gap-3 rounded-lg bg-white/[0.025] border border-white/5 px-3 py-2"
                  data-testid="mood-only-band"
                >
                  <div className="flex items-center gap-2.5">
                    <span className="text-2xl leading-none">
                      {moodSummary.meanRating >= 4.5 ? '🔆'
                        : moodSummary.meanRating >= 3.5 ? '✨'
                        : moodSummary.meanRating >= 2.5 ? '😌'
                        : moodSummary.meanRating >= 1.5 ? '🙂' : '😕'}
                    </span>
                    <div className="leading-tight">
                      <div className="text-white/90 text-sm font-medium tabular-nums">
                        {moodSummary.meanRating?.toFixed(1) ?? '—'}
                        <span className="text-white/35 text-[11px] ml-1">/ 5</span>
                      </div>
                      <div className="text-[10px] uppercase tracking-wider text-white/40">
                        Mood · {moodSummary.totalRatings} rating{moodSummary.totalRatings === 1 ? '' : 's'}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {!loading && !error && hasData && (
            <>
              {/* Summary chips */}
              <div className="grid grid-cols-3 gap-2 text-center">
                <div data-testid="hrv-summary-sessions">
                  <div className="text-white text-xl font-semibold tabular-nums">
                    {summary.totalSessions}
                  </div>
                  <div className="text-[10px] uppercase tracking-wider text-white/40">
                    Sessions
                  </div>
                </div>
                <div data-testid="hrv-summary-mean-rmssd">
                  <div className="text-cyan-200 text-xl font-semibold tabular-nums">
                    {summary.meanRmssd ?? '—'}
                  </div>
                  <div className="text-[10px] uppercase tracking-wider text-white/40">
                    Mean HRV
                  </div>
                </div>
                <div data-testid="hrv-summary-best-rhythm">
                  <div className="text-rose-200 text-xl font-semibold tabular-nums">
                    {summary.bestRhythm ?? '—'}
                  </div>
                  <div className="text-[10px] uppercase tracking-wider text-white/40">
                    Best rhythm
                  </div>
                </div>
              </div>

              {/* Mood band — only rendered when the user has at least one
                  rating. Keeps the dashboard focused on HRV until there's
                  real signal to surface. */}
              {moodSummary && moodSummary.totalRatings > 0 && (
                <div
                  className="flex items-center justify-between gap-3 rounded-lg bg-white/[0.025] border border-white/5 px-3 py-2"
                  data-testid="mood-summary-band"
                >
                  <div className="flex items-center gap-2.5">
                    <span className="text-2xl leading-none" aria-hidden="true">
                      {moodSummary.meanRating >= 4.5 ? '🔆'
                        : moodSummary.meanRating >= 3.5 ? '✨'
                        : moodSummary.meanRating >= 2.5 ? '😌'
                        : moodSummary.meanRating >= 1.5 ? '🙂' : '😕'}
                    </span>
                    <div className="leading-tight">
                      <div className="text-white/90 text-sm font-medium tabular-nums">
                        {moodSummary.meanRating?.toFixed(1) ?? '—'}
                        <span className="text-white/35 text-[11px] ml-1">/ 5</span>
                      </div>
                      <div className="text-[10px] uppercase tracking-wider text-white/40">
                        Mood · {moodSummary.totalRatings} rating{moodSummary.totalRatings === 1 ? '' : 's'}
                      </div>
                    </div>
                  </div>
                  {/* Tiny inline bar chart of the 30-day mood trend */}
                  <div
                    className="flex items-end gap-0.5 h-6"
                    data-testid="mood-mini-trend"
                  >
                    {moodTrend.slice(-14).map((d, i) => {
                      const v = d.avg_rating;
                      const h = v == null ? 2 : Math.max(2, (v / 5) * 24);
                      return (
                        <span
                          key={d.date + i}
                          className={v == null
                            ? 'w-1 rounded-sm bg-white/10'
                            : 'w-1 rounded-sm bg-cyan-300/70'}
                          style={{ height: `${h}px` }}
                        />
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Sparkline */}
              <Sparkline trend={trend} />
              <div className="flex items-center justify-center gap-4 text-[10px] text-white/45">
                <span className="inline-flex items-center gap-1.5">
                  <span className="inline-block w-2 h-0.5 bg-cyan-300" /> HRV (RMSSD)
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="inline-block w-2 h-0.5 bg-rose-400" /> Rhythm
                </span>
              </div>

              {/* Recent snapshots */}
              {snapshots.length > 0 && (
                <div className="space-y-1.5 pt-2 border-t border-white/5">
                  <div className="text-[11px] uppercase tracking-wider text-white/40 mb-1">
                    Recent sessions
                  </div>
                  <ul className="space-y-1" data-testid="hrv-recent-snapshots">
                    {snapshots.slice(0, 5).map((s) => (
                      <li
                        key={s.id}
                        className="flex items-center justify-between text-xs bg-white/[0.02] rounded px-2.5 py-1.5"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="text-white/80 truncate">
                            {s.preset_name || 'Session'} · {formatDuration(s.duration_sec)}
                          </div>
                          <div className="text-white/35 text-[10px]">
                            {formatDateShort(s.created_at)}
                          </div>
                        </div>
                        <div className="flex gap-3 tabular-nums shrink-0">
                          <span className="text-cyan-200">{s.rmssd ? Math.round(s.rmssd) : '—'}<span className="text-white/30 text-[9px] ml-0.5">ms</span></span>
                          <span className="text-rose-200">{s.rhythm_score ? Math.round(s.rhythm_score) : '—'}</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}

          {!loading && (
            <div className="flex justify-end pt-1">
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={load}
                data-testid="hrv-dashboard-refresh"
                className="h-7 px-2 text-[11px] text-white/40 hover:text-white gap-1"
              >
                <RefreshCw className="w-3 h-3" />
                Refresh
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default HrvDashboard;
